/**
 * Editable texts of a Bill of Lading print (HBL / DBL / MBL preview).
 *
 * The print pages first build the texts that would be printed (number, parties with their
 * address lines, route, footer and the cargo lines from buildBlCargoContent); this panel lets
 * the user correct any of them before the PDF is generated again. Nothing here is saved on the
 * file: the corrections only change the printed document.
 */
import React from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const MONO = { fontFamily: "Courier New, Courier, monospace", fontSize: "0.8rem" };

const HEADER_FIELDS = [
    { key: "number", label: "B/L Number" },
    { key: "consignor", label: "Consignor / Shipper", rows: 5 },
    { key: "consignee", label: "Consigned to order of", rows: 5 },
    { key: "notify", label: "Notify address", rows: 5 },
    { key: "placeOfReceipt", label: "Place of receipt", half: true },
    { key: "vesselName", label: "Ocean vessel", half: true },
    { key: "pol", label: "Port of loading", half: true },
    { key: "pod", label: "Port of discharge", half: true },
    { key: "placeOfDelivery", label: "Place of delivery", half: true },
    { key: "freight", label: "Freight payable at", half: true },
    { key: "departure", label: "Place and date of issue", half: true },
];

const sectionTitle = (text) => (
    <MDTypography variant="button" fontWeight="bold" sx={{ display: "block", mt: 2, mb: 1, color: "#0f172a" }}>
        {text}
    </MDTypography>
);

const fieldSx = {
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff", borderRadius: "10px" },
};

// One cargo line: the container / packages text, the description and the two figures.
const CargoLine = ({ line, onChange, withDescription = true }) => (
    <Grid container spacing={1} sx={{ mb: 0.75 }}>
        <Grid item xs={12} md={withDescription ? 5 : 8}>
            <TextField
                fullWidth
                size="small"
                value={line.text || ""}
                onChange={(e) => onChange({ ...line, text: e.target.value })}
                inputProps={{ style: MONO }}
                sx={fieldSx}
            />
        </Grid>
        {withDescription && (
            <Grid item xs={12} md={3}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Description"
                    value={line.desc || ""}
                    onChange={(e) => onChange({ ...line, desc: e.target.value })}
                    inputProps={{ style: MONO }}
                    sx={fieldSx}
                />
            </Grid>
        )}
        <Grid item xs={6} md={2}>
            <TextField
                fullWidth
                size="small"
                placeholder="KGS"
                value={line.kgs || ""}
                onChange={(e) => onChange({ ...line, kgs: e.target.value })}
                inputProps={{ style: MONO }}
                sx={fieldSx}
            />
        </Grid>
        <Grid item xs={6} md={2}>
            <TextField
                fullWidth
                size="small"
                placeholder="CBM"
                value={line.cbm || ""}
                onChange={(e) => onChange({ ...line, cbm: e.target.value })}
                inputProps={{ style: MONO }}
                sx={fieldSx}
            />
        </Grid>
    </Grid>
);

const BlPrintEditor = ({ texts, onChange }) => {
    if (!texts) return null;

    const set = (key, value) => onChange({ ...texts, [key]: value });
    const cargo = texts.cargo || { blocks: [], total: { lines: [] } };

    const setBlockLine = (blockIndex, lineIndex, line) => {
        const blocks = cargo.blocks.map((block, b) =>
            b !== blockIndex
                ? block
                : { ...block, lines: block.lines.map((item, l) => (l === lineIndex ? line : item)) }
        );
        set("cargo", { ...cargo, blocks });
    };
    const setTotalLine = (lineIndex, line) => {
        const lines = (cargo.total?.lines || []).map((item, l) => (l === lineIndex ? line : item));
        set("cargo", { ...cargo, total: { ...cargo.total, lines } });
    };

    return (
        <MDBox>
            <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                Corrections change only this print. Press &quot;Update preview&quot; to see them on the PDF.
            </MDTypography>
            {sectionTitle("Header")}
            <Grid container spacing={1.5}>
                {HEADER_FIELDS.map(({ key, label, rows, half }) => (
                    <Grid item xs={12} md={half ? 6 : 12} key={key}>
                        <TextField
                            fullWidth
                            size="small"
                            label={label}
                            value={texts[key] || ""}
                            onChange={(e) => set(key, e.target.value)}
                            multiline={Boolean(rows)}
                            minRows={rows}
                            InputLabelProps={{ shrink: true }}
                            sx={fieldSx}
                        />
                    </Grid>
                ))}
            </Grid>

            {sectionTitle("Cargo lines")}
            <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1 }}>
                Container / packages · Description · Gross weight · Measurement
            </MDTypography>
            {cargo.blocks.map((block, blockIndex) => (
                <MDBox
                    key={`block-${blockIndex}`}
                    sx={{ mb: 1, pb: 0.5, borderBottom: "1px dashed #e2e8f0" }}
                >
                    {block.lines.map((line, lineIndex) => (
                        <CargoLine
                            key={`line-${blockIndex}-${lineIndex}`}
                            line={line}
                            onChange={(next) => setBlockLine(blockIndex, lineIndex, next)}
                        />
                    ))}
                </MDBox>
            ))}
            {(cargo.total?.lines || []).length > 0 && (
                <>
                    {sectionTitle("Total")}
                    {cargo.total.lines.map((line, lineIndex) => (
                        <CargoLine
                            key={`total-${lineIndex}`}
                            line={line}
                            withDescription={false}
                            onChange={(next) => setTotalLine(lineIndex, next)}
                        />
                    ))}
                </>
            )}
        </MDBox>
    );
};

export default BlPrintEditor;
